import React from 'react';
import { RESULT_CATEGORY } from '../Engine/analysisEngine';
import { getEnumLabel, isEnumVariable } from '../Engine/variableClassifier';

/**
 * ResultVariable — single variable analysis card (v2).
 * Shows OR-block structure per skill (Cond.1, Cond.2...).
 * Supports new PARTIAL_OVERLAP category (orange).
 */

// ── Config per category ────────────────────────────────────────────────────────
const CATEGORY_CONFIG = {
    [RESULT_CATEGORY.INCOMPATIBLE_RACE]: {
        border: 'border-l-4 border-red-500',
        bg: 'bg-slate-800',
        pill: 'bg-red-900/60 text-red-300 border-red-700/50',
        label: 'INCOMPATIBLE',
        icon: '🔴',
    },
    [RESULT_CATEGORY.INCOMPATIBLE_MOMENT]: {
        border: 'border-l-4 border-amber-500',
        bg: 'bg-slate-800',
        pill: 'bg-amber-900/60 text-amber-300 border-amber-700/50',
        label: 'NOT SAME INSTANT',
        icon: '🟡',
    },
    [RESULT_CATEGORY.PARTIAL_OVERLAP]: {
        border: 'border-l-4 border-orange-500',
        bg: 'bg-slate-800',
        pill: 'bg-orange-900/60 text-orange-300 border-orange-700/50',
        label: 'PARTIAL',
        icon: '🟠',
    },
    [RESULT_CATEGORY.COMPATIBLE]: {
        border: 'border-l-4 border-emerald-500',
        bg: 'bg-slate-800',
        pill: 'bg-emerald-900/60 text-emerald-300 border-emerald-700/50',
        label: 'COMPATIBLE',
        icon: '🟢',
    },
    [RESULT_CATEGORY.SUBSET]: {
        border: 'border-l-4 border-violet-500',
        bg: 'bg-slate-800',
        pill: 'bg-violet-900/60 text-violet-300 border-violet-700/50',
        label: 'SUBSET',
        icon: '🔵',
    },
    [RESULT_CATEGORY.NO_CONFLICT]: {
        border: 'border-l-4 border-slate-600',
        bg: 'bg-slate-800/60',
        pill: 'bg-slate-700 text-slate-400 border-slate-600',
        label: 'ONLY ONE',
        icon: '⚪',
    },
};

// ── Condition type badge ───────────────────────────────────────────────────────
const CondTypeBadge = ({ condType }) => (
    <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded border
        ${condType === 'Precondition'
            ? 'bg-orange-900/40 text-orange-300 border-orange-700/40'
            : 'bg-slate-700 text-slate-400 border-slate-600/50'
        }`}>
        {condType === 'Precondition' ? 'Pre' : 'Cond'}
    </span>
);

// ── OR-block label ────────────────────────────────────────────────────────────
const OrBlockLabel = ({ blockIdx, totalBlocks }) => {
    if (totalBlocks <= 1) return null;
    return (
        <span className="text-[8px] font-bold px-1 py-0.5 rounded
            bg-violet-900/30 text-violet-400 border border-violet-700/40">
            OR {blockIdx + 1}
        </span>
    );
};

// ── Single entry row ───────────────────────────────────────────────────────────
const EntryRow = ({ entry }) => {
    // If multiple AND-pairs in same block, show them all
    const pairs = entry.allPairs || [[entry.operator, entry.value]];

    return (
        <div className="flex items-center gap-2 py-1 border-b border-slate-700/40 last:border-0">
            {/* Skill name */}
            <span className="text-[10px] text-slate-400 w-24 truncate flex-shrink-0"
                title={entry.skillName}>
                {entry.skillName}
            </span>
            {/* Cond type */}
            <CondTypeBadge condType={entry.condType} />
            {/* OR block label */}
            <OrBlockLabel blockIdx={entry.blockIdx} totalBlocks={entry.totalBlocks} />
            {/* Operator + value pairs (AND within block) */}
            <span className="font-mono text-[11px] text-slate-200 flex flex-wrap gap-x-2">
                {pairs.map(([op, val], i) => {
                    const label = isEnumVariable(entry.variable)
                        ? getEnumLabel(entry.variable, val) : null;
                    return (
                        <span key={i}>
                            {op}{' '}
                            <span className="text-amber-300">{val}</span>
                            {label && <span className="text-slate-500 ml-0.5">({label})</span>}
                        </span>
                    );
                })}
            </span>
        </div>
    );
};

// ── Range bar for numeric ──────────────────────────────────────────────────────
const RangeBar = ({ domain }) => {
    if (!domain?.range && domain?.type !== 'range') return null;
    const { min, max } = domain;
    if (min === null && max === null) return null;
    const isPercent = max !== null && max <= 100 && min !== null && min >= 0;
    if (!isPercent) return null;

    return (
        <div className="mt-2">
            <div className="relative h-2 bg-slate-700 rounded-full overflow-hidden">
                <div
                    className="absolute h-full bg-gradient-to-r from-violet-500 to-indigo-500 rounded-full"
                    style={{ left: `${min}%`, width: `${(max - min)}%` }}
                />
            </div>
            <div className="flex justify-between text-[9px] font-mono text-slate-500 mt-0.5">
                <span>{min}%</span>
                <span>{max}%</span>
            </div>
        </div>
    );
};

// ── Main component ─────────────────────────────────────────────────────────────
const ResultVariable = ({ result }) => {
    const { variable, variableType, entries, intersection, category, summary } = result;
    const cfg = CATEGORY_CONFIG[category] || CATEGORY_CONFIG[RESULT_CATEGORY.NO_CONFLICT];

    return (
        <div className={`rounded-xl border border-slate-700 overflow-hidden ${cfg.border} ${cfg.bg}`}>
            {/* Header row */}
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-700/50">
                {/* Type badge */}
                <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full border
                    ${variableType === 'race'
                        ? 'bg-blue-900/40 text-blue-300 border-blue-700/40'
                        : 'bg-slate-700 text-slate-400 border-slate-600/50'
                    }`}>
                    {variableType === 'race' ? '🏟 RACE' : '⏱ MOMENT'}
                </span>

                {/* Variable name */}
                <span className="font-mono text-sm font-bold text-slate-100 flex-1">
                    {variable}
                </span>

                {/* Result pill */}
                <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full border uppercase tracking-wider ${cfg.pill}`}>
                    {cfg.icon} {cfg.label}
                </span>
            </div>

            {/* Entries per skill — grouped with OR-block labels */}
            <div className="px-4 py-2">
                {entries.map((entry, i) => (
                    <EntryRow key={`${entry.skillName}-${entry.blockIdx}-${i}`} entry={entry} />
                ))}
            </div>

            {/* Intersection result */}
            {summary && (
                <div className="px-4 pb-3">
                    <p className="text-[10px] text-slate-500 italic">{summary}</p>
                    {intersection && <RangeBar domain={intersection} />}
                </div>
            )}
        </div>
    );
};

export default ResultVariable;
